import React, { useEffect, useState, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { X } from 'lucide-react';
import DashboardModuleShell from '../../components/DashboardModuleShell';
import DataTable from '../../components/DataTable';
import api from '../../services/api';
import alertService from '../../utils/alert';
import * as Icons from 'lucide-react';

export default function ExternalModulePage() {
  const { moduleId } = useParams();
  const [mod, setMod] = useState(null);
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);

  const loadModule = useCallback(async () => {
    try {
      const res = await api.get(`/support/external-modules/${moduleId}`);
      setMod(res.data.data);
    } catch (err) {
      alertService.toast('error', err.response?.data?.error || 'Failed to load this module');
    }
  }, [moduleId]);

  const loadRecords = useCallback(async (searchValue) => {
    setLoading(true);
    try {
      const res = await api.get(`/support/external-modules/${moduleId}/records`, {
        params: searchValue ? { search: searchValue } : {},
      });
      setRecords(res.data.data || []);
    } catch (err) {
      alertService.toast('error', err.response?.data?.error || 'Failed to load records');
      setRecords([]);
    } finally {
      setLoading(false);
    }
  }, [moduleId]);

  useEffect(() => { loadModule(); }, [loadModule]);
  useEffect(() => { loadRecords(); }, [loadRecords]);

  const handleSearch = (value) => {
    setSearch(value);
    loadRecords(value);
  };

  const openDetail = async (row) => {
    if (!mod?.detailApiToolId || !mod?.recordIdField) return;
    const recordId = row[mod.recordIdField];
    if (recordId === undefined) return;
    setDetail({});
    setDetailLoading(true);
    try {
      const res = await api.get(`/support/external-modules/${moduleId}/records/${encodeURIComponent(recordId)}`);
      setDetail(res.data.data);
    } catch (err) {
      alertService.toast('error', err.response?.data?.error || 'Failed to load record detail');
      setDetail(null);
    } finally {
      setDetailLoading(false);
    }
  };

  const columns = (records[0] ? Object.keys(records[0]) : []).map(key => ({
    header: key,
    accessor: key,
    render: (val) => (val === null || val === undefined) ? '—' : (typeof val === 'object' ? JSON.stringify(val) : String(val)),
  }));

  const Icon = Icons[mod?.icon] || Icons.Plug;

  return (
    <DashboardModuleShell
      title={mod?.name || 'External Module'}
      subtitle={mod ? `Live data from your connected ${mod.group} API.` : ''}
      icon={Icon}
    >
      <DataTable
        columns={columns}
        data={records}
        loading={loading}
        onRowClick={mod?.detailApiToolId ? openDetail : undefined}
        searchValue={search}
        onSearchChange={mod?.searchParamName ? handleSearch : undefined}
        emptyMessage="No records returned by the connected API."
      />

      {detail && (
        <div className="fixed inset-0 bg-black/40 flex items-end sm:items-center justify-center z-50 sm:p-4" onClick={() => setDetail(null)}>
          <div className="bg-white rounded-t-lg sm:rounded-lg shadow-[0_2px_8px_rgba(0,0,0,0.16)] w-full max-w-lg max-h-[100dvh] overflow-y-auto p-4 sm:p-6" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-[16px] font-bold text-[#2E2E2E]">Record Detail</h3>
              <button onClick={() => setDetail(null)}><X className="w-5 h-5" /></button>
            </div>
            {detailLoading ? (
              <p className="text-[13px] text-[#706E6B]">Loading…</p>
            ) : (
              <div className="space-y-2">
                {Object.entries(detail).map(([key, val]) => (
                  <div key={key} className="flex justify-between gap-4 text-[13px] border-b border-[#D8DDE6] pb-2">
                    <span className="font-bold text-[#706E6B]">{key}</span>
                    <span className="text-[#080707] text-right break-all">{val === null || val === undefined ? '—' : String(val)}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </DashboardModuleShell>
  );
}
